import {
  useEffect,
  useMemo,
  Ref,
  forwardRef,
  useImperativeHandle,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import ScreenCapture, { ScreenCaptureOptions } from "@uppy/screen-capture";
import XHRUpload, { XHRUploadOptions } from "@uppy/xhr-upload";
import { useQueryClient } from "@tanstack/react-query";
import Uppy, { UploadResult } from "@uppy/core";
import GoogleDrive from "@uppy/google-drive";
import { useParams } from "react-router-dom";
import { Dashboard } from "@uppy/react";
import Instagram from "@uppy/instagram";
import Unsplash from "@uppy/unsplash";
import Facebook from "@uppy/facebook";
import OneDrive from "@uppy/onedrive";
import { useAtomValue } from "jotai";
import Dropbox from "@uppy/dropbox";
import { Button } from "reactstrap";
import Webcam from "@uppy/webcam";
import Audio from "@uppy/audio";
import Cookies from "js-cookie";
import Zoom from "@uppy/zoom";
import Box from "@uppy/box";
import dayjs from "dayjs";

import FileExplorer from "../../views/fileExplorer/FileExplorer";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import { FileInterface } from "../../types/RecordInterfaces";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import useUser from "../../api/api-hooks/auth/useUser";
import Constants from "../../utils/Constants";
import StrUtils from "../../utils/StrUtils";

export type FileType =
  | "Image"
  | "Video"
  | "Audio"
  | "Media"
  | "Text"
  | "Data"
  | "SingleFile"
  | null;

interface Props {
  fileType?: FileType;
  plugins?: string[];
  height?: number;
  width?: number;
  endpoint?: string;
  meta?: {
    isPublic?: boolean;
    sources?: any[];
  };
  multiFiles?: boolean;
  includeRaw?: boolean;
  hidden?: boolean;
  beforeSubmit?: (data?: any) => any;
  doneButtonHandler?: () => void;
  afterSubmit: (
    files: UppyFileInterface | UppyFileInterface[],
    raw?: UploadResult<any, Record<string, string>>
  ) => void;
  customOnComplete?: (
    result: UploadResult<any, Record<string, string>>
  ) => void;
  setKeepOpen?: Dispatch<SetStateAction<boolean>>;
  showFileExplorerButton?: boolean;
  accept?: string[];
  fileTypes?: string[];
  /** An array of files to filter them from the file explorer. */
  filesToFilter?: FileInterface[];
}

export interface UppyFileInterface {
  AltText: string;
  Caption: string;
  File: string;
  Name: string;
  PublicURL?: string;
  Transcript?: string;
  Index?: boolean;
  RecordID: string;
  Size: number;
  Type: string;
  UploadedBy: string;
  UploadedDate: string;
  id: string;
}

function UppyFile(
  {
    fileType = null,
    plugins = [
      "Audio",
      "Webcam",
      "ScreenCapture",
      "GoogleDrive",
      "OneDrive",
      "Box",
    ],
    endpoint,
    meta = {
      isPublic: false,
      sources: [],
    },
    height = 300,
    width = 334,
    multiFiles = false,
    includeRaw = false,
    hidden = false,
    beforeSubmit,
    afterSubmit,
    doneButtonHandler,
    customOnComplete,
    setKeepOpen,
    showFileExplorerButton = true,
    accept,
    fileTypes,
    filesToFilter,
  }: Props,
  ref: Ref<any>
) {
  const client = useQueryClient();
  const tenantIDAtomValue = useAtomValue(tenantIDAtom);
  const { tenantID: tenantIDURL } = useParams();
  const tenantID = tenantIDAtomValue || tenantIDURL;
  const [showFileExplorer, setShowFileExplorer] = useState(false);

  const uppy = useMemo(() => {
    if (!tenantID) return;

    let allowedFileTypes: string[] | null;
    switch (fileType) {
      case "Image":
        allowedFileTypes = Constants.FILETYPES.IMAGE;
        break;
      case "Video":
        allowedFileTypes = Constants.FILETYPES.VIDEO;
        break;
      case "Audio":
        allowedFileTypes = Constants.FILETYPES.AUDIO;
        break;
      case "Text":
        allowedFileTypes = Constants.FILETYPES.TEXT;
        break;
      case "Data":
        allowedFileTypes = Constants.FILETYPES.DATA;
        break;
      case "Media":
        allowedFileTypes = [
          ...Constants.FILETYPES.IMAGE,
          ...Constants.FILETYPES.VIDEO,
          ...Constants.FILETYPES.AUDIO,
        ];
        break;
      default:
        allowedFileTypes = null;
    }

    if (accept) allowedFileTypes = accept;

    let companionURL = "https://upload.growthcloud.com/companion";

    if (
      window.location.href.indexOf("localhost") >= 0 ||
      window.location.href.indexOf("127.0.0.1") >= 0
    ) {
      companionURL = `http://localhost:8081/companion`;
    }

    const companionConfig = {
      companionUrl: companionURL,
    };

    const oneDriveConfig = {
      companionUrl: companionURL,
      title: "Microsoft Office",
    };

    const screenCaptureConfig: ScreenCaptureOptions = {
      displayMediaConstraints: {
        video: {
          width: 1920,
          height: 1080,
          frameRate: {
            ideal: 3,
            max: 5,
          },
          displaySurface: "monitor",
        },
      },
      userMediaConstraints: {
        audio: true,
      },
      preferredVideoMimeType: "video/mp4",
    };

    const finalToken = Cookies.get(`authenticationToken_${tenantID}`) || "";

    const localConfig: XHRUploadOptions<any, any> = {
      endpoint: `${(window as any).GC_API_URL}${endpoint ?? "/tenant/file"}`,
      headers: {
        "app-id": "GrowthCloud",
        Authorization: "Bearer " + finalToken,
      },
      formData: true,
      withCredentials: true,
    };

    const uppyInstance = new Uppy({
      debug: true,
      restrictions: {
        allowedFileTypes: allowedFileTypes,
        ...(!multiFiles && { maxNumberOfFiles: 1 }),
      },
      onBeforeUpload: (data: any) => {
        if (beforeSubmit) {
          beforeSubmit(data);
          return true;
        }
        return true;
      },
      meta: meta,
    });

    // Latest version of @uppy/audio is not supported

    if (plugins.includes("ScreenCapture")) {
      uppyInstance.use(ScreenCapture, screenCaptureConfig);
    }
    if (plugins.includes("GoogleDrive")) {
      uppyInstance.use(GoogleDrive, companionConfig);
    }
    if (plugins.includes("OneDrive")) {
      uppyInstance.use(OneDrive, oneDriveConfig);
      //@ts-ignore
      uppyInstance.getPlugin("OneDrive").title = "Microsoft Office & OneDrive";
    }
    if (plugins.includes("Facebook")) {
      uppyInstance.use(Facebook, companionConfig);
    }
    if (plugins.includes("Dropbox")) {
      uppyInstance.use(Dropbox, companionConfig);
    }
    if (plugins.includes("Box")) {
      // Latest version of @uppy/box is not supported
      uppyInstance.use(Box, companionConfig);
    }
    if (plugins.includes("Zoom")) {
      uppyInstance.use(Zoom, companionConfig);
    }
    if (plugins.includes("Unsplash")) {
      uppyInstance.use(Unsplash, companionConfig);
    }
    if (plugins.includes("Webcam")) {
      uppyInstance.use(Webcam, { id: "Webcam", ...companionConfig });

      //@ts-ignore
      uppyInstance.getPlugin("Webcam").title = "Capture Photo & Video";
    }
    if (plugins.includes("Instagram")) {
      uppyInstance.use(Instagram, companionConfig);
    }
    if (plugins.includes("Audio")) {
      uppyInstance.use(Audio, {});

      //@ts-ignore
      uppyInstance.getPlugin("Audio").title = "Record Audio";
    }

    uppyInstance.use(XHRUpload, localConfig);

    return uppyInstance;
  }, [fileType, tenantID, accept]);

  const { user } = useUser();

  useEffect(() => {
    if (!uppy) return;

    const onComplete = (result: UploadResult<any, Record<string, string>>) => {
      client.invalidateQueries({
        queryKey: [tenantID, "records", "Files"],
      });

      if (customOnComplete) {
        customOnComplete(result);
        return;
      }

      if (includeRaw) {
        afterSubmit([], result);
        return;
      }

      if (
        (result.failed && result?.failed?.length > 0) ||
        !result.successful ||
        result.successful.length === 0
      )
        throw new Error("File upload Failed");

      const newFiles: UppyFileInterface[] = [];
      for (const item of result?.successful) {
        //@ts-ignore
        const file = item?.response?.body?.data?.paths[0];
        //@ts-ignore
        const url = item?.response?.body?.data?.urls[0];
        //@ts-ignore
        const name = item?.response?.body?.data?.name;
        const type = item?.data.type;
        const size = item?.data.size;
        //@ts-ignore
        const recordID = item?.response?.body?.data?.recordID;
        const newFile: UppyFileInterface = {
          id: StrUtils.uuidv4(),
          File: file,
          AltText: "description",
          Caption: "",
          Name: name,
          Size: size,
          UploadedDate: dayjs().format(),
          UploadedBy: user?.id,
          // text: item.data.text,
          Type: type,
          RecordID: recordID,
          ...(url && { PublicURL: url }),
        };
        newFiles.push(newFile);
      }

      afterSubmit(
        multiFiles ? newFiles : newFiles[0],
        includeRaw ? result : undefined
      );
    };

    uppy.on("complete", onComplete);

    uppy.on("error", (error) => {
      console.error(error);
    });

    return () => {
      uppy.off("complete", onComplete);
    };
  }, [uppy]);

  useEffect(() => {
    () => {
      if (uppy) {
        // @ts-ignore
        uppy.close({ reason: "unmount" });
      }
    };
    return;
  }, [uppy]);

  useEffect(() => {
    if (!uppy) return;

    uppy.setMeta(meta);
  }, [meta, uppy]);

  useImperativeHandle(
    ref,
    () => ({
      addFile: (file: File) => {
        uppy?.addFile(file);
      },
      uploadFiles: async () => {
        return await uppy?.upload();
      },
      getFiles: () => uppy?.getFiles(),
      clear: () => uppy?.clear(),
    }),
    [uppy]
  );

  if (!uppy) return null;

  return (
    <div className={`tw:flex tw:flex-col ${hidden ? "tw:hidden" : ""}`}>
      <Dashboard
        className={`gc-uppy-uploader`}
        uppy={uppy}
        plugins={plugins}
        height={height}
        width={width}
        thumbnailWidth={150}
        doneButtonHandler={() => {
          callFunctionIfExists(doneButtonHandler);
          uppy.cancelAll();
        }}
      />
      {/* GC File Explorer Intergration :-) */}
      {showFileExplorerButton && (
        <>
          <Button
            className="tw:mt-3 tw:mx-2 tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-[13px]"
            size="sm"
            color="secondary"
            onClick={() => {
              setShowFileExplorer(true);
              setKeepOpen && setKeepOpen(true);
            }}
          >
            <i className="mdi mdi-folder-table tw:text-primary tw:text-xl" />
            Growth Drive
          </Button>
          <FileExplorer
            open={showFileExplorer}
            onClose={() => {
              setShowFileExplorer(false);
              setKeepOpen && setKeepOpen(false);
            }}
            onAddFiles={(newFiles: any) => {
              afterSubmit(multiFiles ? newFiles : newFiles[0]);
            }}
            multiFiles={multiFiles}
            fileType={fileType}
            fileTypes={fileTypes}
            isPrivate={!meta.isPublic}
            filesToFilter={filesToFilter}
          />
        </>
      )}
    </div>
  );
}

export default forwardRef(UppyFile);
